import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Radar, Loader2, RefreshCw, PlayCircle, Search, CheckCircle2, XCircle, PencilLine,
  ShieldCheck, Database, Gauge,
} from "lucide-react";

const Stat = ({ label, value, accent }) => (
  <div className="tc-card p-4">
    <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted">{label}</div>
    <div className={`mt-1 text-2xl font-bold ${accent || "text-tc-txt"}`}>{value}</div>
  </div>
);

export default function AdminOrganic() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [dash, setDash] = useState(null);
  const [busy, setBusy] = useState("");
  const [opps, setOpps] = useState([]);
  const [cands, setCands] = useState([]);
  const [detail, setDetail] = useState(null);
  const [calib, setCalib] = useState(null);
  const [costs, setCosts] = useState([]);

  const isAdmin = !!user?.is_admin;

  const loadDash = useCallback(() => {
    api.get("/organic/dashboard").then((r) => setDash(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { if (isAdmin) loadDash(); }, [isAdmin, loadDash]);

  useEffect(() => {
    if (!isAdmin) return;
    if (tab === "pipeline") api.get("/organic/opportunities").then((r) => setOpps(r.data.opportunities || [])).catch(() => {});
    if (tab === "review") api.get("/organic/candidates").then((r) => setCands(r.data.candidates || [])).catch(() => {});
    if (tab === "calibration") api.get("/organic/calibration-report").then((r) => setCalib(r.data)).catch(() => {});
    if (tab === "costs") api.get("/organic/costs").then((r) => setCosts(r.data.items || [])).catch(() => {});
  }, [tab, isAdmin]);

  if (!isAdmin) {
    return (
      <div className="p-8" data-testid="organic-forbidden">
        <div className="tc-card p-6 max-w-md">
          <ShieldCheck className="w-6 h-6 text-tc-bear" />
          <h1 className="mt-3 font-heading text-xl font-bold">Admin only</h1>
          <p className="mt-2 text-sm text-tc-sub">Organic Intelligence administration requires a global admin account. Guardian status does not grant access.</p>
          <button onClick={() => navigate("/dashboard")} className="mt-4 text-sm text-tc-coach hover:underline">← Back to dashboard</button>
        </div>
      </div>
    );
  }

  const run = async (name, fn) => {
    setBusy(name);
    try { await fn(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const setConfig = (patch) => run("config", async () => {
    const { data } = await api.post("/organic/config", patch);
    setDash((d) => ({ ...d, config: data }));
    toast.success("Configuration updated");
  });

  const decide = (id, action) => run("decide", async () => {
    await api.post(`/organic/candidate/${id}/decision`, { action });
    toast.success(`Marked ${action}`);
    setDetail(null);
    api.get("/organic/candidates").then((r) => setCands(r.data.candidates || []));
    loadDash();
  });

  const openDetail = (id) => run("detail", async () => {
    const { data } = await api.get(`/organic/candidate/${id}`);
    setDetail(data);
  });

  if (!dash) return <div className="p-8 text-tc-muted font-mono text-sm">Loading Organic Intelligence…</div>;
  const cfg = dash.config || {};

  const TABS = [["overview", "Overview"], ["pipeline", "Pipeline"], ["review", "Review"], ["calibration", "Calibration"], ["costs", "Costs"]];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6" data-testid="organic-admin">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Radar className="w-6 h-6 text-tc-coach" /> Organic Intelligence
          </h1>
          <p className="text-tc-sub mt-1 text-sm">Autonomous discovery → research → create → verify → review. Calibration mode holds every candidate for your approval.</p>
        </div>
        <button data-testid="organic-refresh" onClick={loadDash} className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-tc-sub hover:border-white/30 flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </header>

      {/* status strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="tc-card p-4" data-testid="organic-mode">
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted">Mode</div>
          <div className={`mt-1 text-lg font-bold ${cfg.mode === "AUTOPUBLISH" ? "text-tc-bull" : "text-tc-xp"}`}>{cfg.mode}</div>
          <div className="text-[11px] text-tc-muted mt-0.5">Autopublish {cfg.mode === "AUTOPUBLISH" ? "ON" : "OFF"}</div>
        </div>
        <div className="tc-card p-4" data-testid="organic-research-status">
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted">Research</div>
          <div className="mt-1 text-lg font-bold text-tc-muted">Disabled</div>
        </div>
        <div className="tc-card p-4" data-testid="organic-llm-status">
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted">Writer (Claude)</div>
          <div className={`mt-1 text-lg font-bold ${dash.llm_available ? "text-tc-bull" : "text-tc-bear"}`}>{dash.llm_available ? "READY" : "UNAVAILABLE"}</div>
          <div className="text-[11px] text-tc-muted mt-0.5">{cfg.writer_model}</div>
        </div>
        <Stat label="Published" value={dash.published} accent="text-tc-coach" />
      </div>

      <nav className="flex gap-1 border-b border-white/10">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`organic-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${tab === k ? "border-tc-coach text-tc-coach" : "border-transparent text-tc-sub hover:text-tc-txt"}`}>{l}</button>
        ))}
      </nav>

      {tab === "overview" && (
        <div className="space-y-5">
          <div className="tc-card p-5 space-y-4">
            <h2 className="font-heading font-semibold">Configuration</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="flex items-center justify-between gap-3 text-sm">
                <span>Generation paused</span>
                <input data-testid="organic-generation-paused" type="checkbox" checked={!!cfg.generation_paused} onChange={(e) => setConfig({ generation_paused: e.target.checked })} className="h-5 w-5 accent-tc-coach" />
              </label>
              <label className="flex items-center justify-between gap-3 text-sm">
                <span>Publishing paused</span>
                <input data-testid="organic-publishing-paused" type="checkbox" checked={!!cfg.publishing_paused} onChange={(e) => setConfig({ publishing_paused: e.target.checked })} className="h-5 w-5 accent-tc-coach" />
              </label>
              <label className="flex items-center justify-between gap-3 text-sm">
                <span>Quality threshold</span>
                <input data-testid="organic-quality-threshold" type="number" min="0" max="100" value={cfg.quality_threshold} onChange={(e) => setConfig({ quality_threshold: parseInt(e.target.value || "90", 10) })} className="w-20 rounded bg-tc-bg border border-white/10 px-2 py-1 text-right" />
              </label>
              <label className="flex items-center justify-between gap-3 text-sm">
                <span>Weekly candidate ceiling</span>
                <input data-testid="organic-ceiling" type="number" min="0" max="50" value={cfg.weekly_candidate_ceiling} onChange={(e) => setConfig({ weekly_candidate_ceiling: parseInt(e.target.value || "5", 10) })} className="w-20 rounded bg-tc-bg border border-white/10 px-2 py-1 text-right" />
              </label>
            </div>
            <div className="text-[12px] text-tc-muted border-t border-white/8 pt-3">
              Calibration reviewed: <b className="text-tc-txt">{cfg.calibration_reviewed || 0}</b> / {cfg.calibration_target || 25}. Autopublish is enabled only by explicit admin action after calibration.
            </div>
          </div>

          <div className="tc-card p-5">
            <h2 className="font-heading font-semibold mb-2">Measurement adapters</h2>
            <div className="grid sm:grid-cols-3 gap-3">
              {Object.entries(dash.metrics || {}).map(([k, v]) => (
                <div key={k} className="rounded-lg bg-white/[0.03] border border-white/10 p-3" data-testid={`organic-metric-${k}`}>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted">{k.replace(/_/g, " ")}</div>
                  <div className="mt-1 text-sm font-semibold text-tc-bear">{v.status}</div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-tc-muted mt-3">Real performance data feeds the improvement loop once connected. No metrics are fabricated.</p>
          </div>
        </div>
      )}

      {tab === "pipeline" && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <button data-testid="organic-discover" disabled={busy === "discover"} onClick={() => run("discover", async () => { const { data } = await api.post("/organic/discover"); toast.success(`Discovered ${data.created?.length || 0} opportunities`); api.get("/organic/opportunities").then((r) => setOpps(r.data.opportunities || [])); loadDash(); })}
              className="rounded-lg bg-tc-coach/15 border border-tc-coach/40 text-tc-coach px-4 py-2 text-sm font-medium hover:bg-tc-coach/25 disabled:opacity-50 flex items-center gap-2">{busy === "discover" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Discover</button>
            <button data-testid="organic-run-pipeline" disabled={busy === "pipeline"} onClick={() => run("pipeline", async () => { const { data } = await api.post("/organic/pipeline/run"); toast.success(`Pipeline: ${(data.steps || []).join(", ") || "no-op"}`); api.get("/organic/opportunities").then((r) => setOpps(r.data.opportunities || [])); loadDash(); })}
              className="rounded-lg bg-white/5 border border-white/10 text-tc-txt px-4 py-2 text-sm font-medium hover:border-white/30 disabled:opacity-50 flex items-center gap-2">{busy === "pipeline" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} Run one tick</button>
          </div>
          <div className="tc-card p-0 overflow-hidden" data-testid="organic-opportunities">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-tc-muted border-b border-white/10"><th className="p-3">Intent</th><th className="p-3">Cluster</th><th className="p-3">Status</th><th className="p-3">Score</th></tr></thead>
              <tbody>
                {opps.length === 0 && <tr><td colSpan="4" className="p-4 text-tc-muted">No opportunities yet — run Discover.</td></tr>}
                {opps.map((o) => (
                  <tr key={o.id} className="border-b border-white/5"><td className="p-3 text-tc-txt">{o.primary_intent}</td><td className="p-3 text-tc-sub">{o.topic_cluster}</td><td className="p-3"><span className="font-mono text-xs text-tc-coach">{o.status}</span></td><td className="p-3">{o.opportunity_score ?? "—"}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "review" && (
        <div className="space-y-3" data-testid="organic-candidates">
          {cands.length === 0 && <div className="tc-card p-6 text-tc-muted text-sm">No candidates held for review. In calibration mode, verified candidates land here for your decision.</div>}
          {cands.map((c) => (
            <div key={c.id} className="tc-card p-4 flex items-center justify-between gap-3" data-testid={`organic-candidate-${c.id}`}>
              <div>
                <div className="font-semibold text-tc-txt">{c.title}</div>
                <div className="text-xs text-tc-muted">{c.topic_cluster} · intent: {c.primary_intent} · quality {c.quality_score ?? "—"}</div>
              </div>
              <button data-testid={`organic-open-${c.id}`} onClick={() => openDetail(c.id)} className="text-sm text-tc-coach hover:underline">Inspect →</button>
            </div>
          ))}
        </div>
      )}

      {tab === "calibration" && calib && (
        <div className="tc-card p-5 space-y-3" data-testid="organic-calibration">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Reviewed" value={`${calib.reviewed}/${calib.target}`} />
            <Stat label="Approval" value={`${calib.approval_rate}%`} accent="text-tc-bull" />
            <Stat label="Revision" value={`${calib.revision_rate}%`} accent="text-tc-xp" />
            <Stat label="Rejection" value={`${calib.rejection_rate}%`} accent="text-tc-bear" />
          </div>
          <div className="text-sm text-tc-sub">QA avg {calib.qa_score_avg} (min {calib.qa_score_min} / max {calib.qa_score_max})</div>
          <div className={`text-sm font-medium ${calib.autopublish_enabled ? "text-tc-bull" : "text-tc-xp"}`} data-testid="organic-calib-reco">{calib.recommendation}</div>
          {!calib.autopublish_enabled && (
            <button data-testid="organic-enable-autopublish" onClick={() => run("enable", async () => { if (!window.confirm("Enable AUTOPUBLISH? Candidates that pass all gates will publish without review.")) return; await api.post("/organic/autopublish/enable"); toast.success("Autopublish enabled"); loadDash(); api.get("/organic/calibration-report").then((r) => setCalib(r.data)); })}
              className="rounded-lg bg-tc-bull/10 border border-tc-bull/40 text-tc-bull px-4 py-2 text-sm font-medium hover:bg-tc-bull/20">Enable autopublish (explicit)</button>
          )}
        </div>
      )}

      {tab === "costs" && (
        <div className="tc-card p-0 overflow-hidden" data-testid="organic-costs">
          <table className="w-full text-xs">
            <thead><tr className="text-left uppercase tracking-wider text-tc-muted border-b border-white/10"><th className="p-3">When</th><th className="p-3">Kind</th><th className="p-3">Provider</th><th className="p-3">Model</th><th className="p-3">ms</th><th className="p-3">Error</th></tr></thead>
            <tbody>
              {costs.length === 0 && <tr><td colSpan="6" className="p-4 text-tc-muted">No provider calls logged yet.</td></tr>}
              {costs.map((c) => (
                <tr key={c.id} className="border-b border-white/5"><td className="p-3 text-tc-muted">{(c.at || "").slice(0, 19)}</td><td className="p-3">{c.kind}</td><td className="p-3">{c.provider}</td><td className="p-3">{c.model}</td><td className="p-3">{c.latency_ms}</td><td className="p-3 text-tc-bear">{c.error ? "yes" : ""}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* candidate detail (provenance) */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="organic-detail">
          <div className="absolute inset-0 bg-black/70" onClick={() => setDetail(null)} />
          <div className="relative tc-card p-6 max-w-3xl w-full my-8 space-y-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-tc-coach">{detail.resource?.topic_cluster}</div>
                <h3 className="mt-1 font-heading text-xl font-bold">{detail.resource?.title}</h3>
                <p className="text-xs text-tc-muted mt-1">Target intent: {detail.target_intent}</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-tc-muted hover:text-tc-txt"><XCircle className="w-5 h-5" /></button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg bg-white/[0.03] border border-white/10 p-3">
                <div className="text-[11px] font-mono uppercase text-tc-muted flex items-center gap-1"><Gauge className="w-3.5 h-3.5" /> Why chosen</div>
                <div className="mt-1 text-tc-sub">Score: {detail.why_chosen?.opportunity_score ?? "—"} · {detail.why_chosen?.classification}</div>
              </div>
              <div className="rounded-lg bg-white/[0.03] border border-white/10 p-3">
                <div className="text-[11px] font-mono uppercase text-tc-muted flex items-center gap-1"><Database className="w-3.5 h-3.5" /> Cost</div>
                <div className="mt-1 text-tc-sub">{detail.estimated_cost?.calls} calls · {detail.estimated_cost?.latency_ms_total} ms</div>
              </div>
            </div>

            {detail.what_tradecraft_adds && <div className="text-sm"><span className="text-tc-muted">TRADECRAFT angle:</span> <span className="text-tc-sub">{detail.what_tradecraft_adds}</span></div>}
            {detail.whats_missing?.length > 0 && (
              <div className="text-sm"><div className="text-tc-muted mb-1">Gaps this fills:</div><ul className="list-disc pl-5 text-tc-sub space-y-1">{detail.whats_missing.map((g, i) => <li key={i}>{g}</li>)}</ul></div>
            )}

            {/* QA gates */}
            {detail.qa && (
              <div className="rounded-lg border border-white/10 p-3" data-testid="organic-qa">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-mono uppercase text-tc-muted">Quality gates</div>
                  <div className={`text-sm font-bold ${detail.qa.mandatory_pass ? "text-tc-bull" : "text-tc-bear"}`}>Score {detail.qa.quality_score} · mandatory {detail.qa.mandatory_pass ? "PASS" : "FAIL"}</div>
                </div>
                <div className="mt-2 grid sm:grid-cols-2 gap-1.5 text-xs">
                  {detail.qa.reviewers && Object.entries(detail.qa.reviewers).map(([role, r]) => (
                    <div key={role} className="flex items-center justify-between border-b border-white/5 py-1">
                      <span className="text-tc-sub">{role}</span>
                      <span className={r.decision === "PASS" ? "text-tc-bull" : r.decision === "FAIL" ? "text-tc-bear" : "text-tc-xp"}>{r.decision} ({r.score})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* body preview */}
            <div className="max-h-64 overflow-y-auto rounded-lg bg-tc-bg border border-white/10 p-3 text-sm text-tc-sub whitespace-pre-wrap" data-testid="organic-body-preview">
              {(detail.resource?.body_markdown || "").slice(0, 2000) || "No body."}
            </div>

            <div className="flex gap-3 pt-2 border-t border-white/8">
              <button data-testid="organic-approve" disabled={busy === "decide"} onClick={() => decide(detail.resource.id, "approve")} className="flex-1 rounded-lg bg-tc-bull/15 border border-tc-bull/40 text-tc-bull font-semibold py-2.5 text-sm hover:bg-tc-bull/25 disabled:opacity-50 flex items-center justify-center gap-2"><CheckCircle2 className="w-4 h-4" /> Approve & publish</button>
              <button data-testid="organic-revise" disabled={busy === "decide"} onClick={() => decide(detail.resource.id, "revise")} className="flex-1 rounded-lg bg-tc-xp/15 border border-tc-xp/40 text-tc-xp font-semibold py-2.5 text-sm hover:bg-tc-xp/25 disabled:opacity-50 flex items-center justify-center gap-2"><PencilLine className="w-4 h-4" /> Revise</button>
              <button data-testid="organic-reject" disabled={busy === "decide"} onClick={() => decide(detail.resource.id, "reject")} className="flex-1 rounded-lg bg-tc-bear/15 border border-tc-bear/40 text-tc-bear font-semibold py-2.5 text-sm hover:bg-tc-bear/25 disabled:opacity-50 flex items-center justify-center gap-2"><XCircle className="w-4 h-4" /> Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
